import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getAccountContacts, updateAccountContact } from '@/services/api';
import { useState } from 'react';
import AccountContactFilter from './AccountContactFilter';
import { ContactForm } from '../../components/forms/ContactForm';
import type { ContactFormData } from '../../components/forms/ContactForm';
import type { AccountContact } from '../../types';
import { useToast } from '../../contexts/ToastContext';
import { EditTrackingProvider } from '../../contexts/EditTrackingContext';

interface Props {
  accountId: string;
}

export default function AccountContactList({ accountId }: Props) {
  const [employmentFilter, setEmploymentFilter] = useState<string>('all');
  const [editingContact, setEditingContact] = useState<AccountContact | null>(null);
  const queryClient = useQueryClient();
  const toast = useToast();

  const { data: contacts, isLoading } = useQuery({
    queryKey: ['accountContacts', accountId, employmentFilter],
    queryFn: () => getAccountContacts(accountId, employmentFilter === 'all' ? undefined : employmentFilter),
  });

  const updateContactMutation = useMutation({
    mutationFn: (data: ContactFormData) => 
      updateAccountContact(accountId, editingContact!.accountContactId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accountContacts', accountId] });
      setEditingContact(null);
      toast.success('Contact updated successfully');
    },
    onError: (error: any) => {
      const message = error.response?.data?.error || error.message || 'Failed to update contact';
      toast.error(message);
    },
  });

  if (isLoading) return <p className="text-brand-text-secondary text-sm">Loading contacts…</p>;

  return (
    <div className="space-y-4">
      <AccountContactFilter value={employmentFilter} onChange={setEmploymentFilter} />
      
      {!contacts || contacts.length === 0 ? (
        <p className="text-brand-text-secondary text-sm">No contacts found.</p>
      ) : (
        <ul className="space-y-2">
          {contacts.map(c => (
            <li key={c.accountContactId} className="bg-white rounded shadow px-4 py-3 text-sm">
              <div className="grid grid-cols-3 gap-2 items-center mb-2">
                <span className="font-medium">{c.firstName} {c.lastName}</span>
                <span className="text-brand-text-secondary">{c.email}</span>
                <span className="text-brand-text-secondary">{c.phone}</span>
              </div>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                  {c.role && (
                    <span className="text-xs text-brand-text-secondary">
                      Role: {c.role}
                    </span>
                  )}
                  <span className={`text-xs px-2 py-0.5 rounded-full ${c.status === 'Active' ? 'bg-brand-accent/15 text-brand-accent' : 'bg-gray-100 text-gray-500'}`}>
                    {c.status}
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${c.isEmployed ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}`}>
                    {c.isEmployed ? '✓ Employed' : '○ Not Employed'}
                  </span>
                  {c.isDefault && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">
                      Default Contact
                    </span>
                  )}
                  {/* T079: Display incomplete badge for auto-generated contacts */}
                  {c.isAutoGeneratedIncomplete && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700">
                      ⚠ Incomplete
                    </span>
                  )}
                </div>
                <button
                  onClick={() => setEditingContact(c)}
                  className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                >
                  Edit
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Edit Contact Modal */}
      {editingContact && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Edit Contact
              </h2>
              <EditTrackingProvider>
                <ContactForm
                  defaultValues={{
                    firstName: editingContact.firstName,
                    lastName: editingContact.lastName,
                    email: editingContact.email,
                    phone: editingContact.phone,
                    role: editingContact.role || undefined,
                    idNumber: editingContact.idNumber || undefined,
                    dateOfBirth: editingContact.dateOfBirth || undefined,
                    isEmployed: editingContact.isEmployed,
                  }}
                  onSubmit={updateContactMutation.mutate}
                  onCancel={() => setEditingContact(null)}
                  isLoading={updateContactMutation.isPending}
                  submitLabel="Update Contact"
                  mode="edit"
                />
              </EditTrackingProvider>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
